import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProducts, getEntry, saveEntry } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function DailyEntry() {
  const { selectedFactory } = useAuth();
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEntry, setExistingEntry] = useState(null);

  useEffect(() => {
    if (!selectedFactory) return;
    getProducts(selectedFactory.id)
      .then(res => setProducts(res.data))
      .catch(console.error);
  }, [selectedFactory]);

  const loadEntry = useCallback(async () => {
    if (!selectedFactory || !date) return;
    setLoading(true);
    try {
      const res = await getEntry(selectedFactory.id, date);
      setExistingEntry(res.data);
      const q = {};
      res.data.items?.forEach(item => { q[item.product_id] = item.quantity; });
      setQuantities(q);
      setNotes(res.data.notes || '');
    } catch (err) {
      if (err.response?.status === 404) {
        setExistingEntry(null);
        setQuantities({});
        setNotes('');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, date]);

  useEffect(() => { loadEntry(); }, [loadEntry]);

  const handleQty = (productId, val) => {
    const n = parseInt(val);
    setQuantities(q => ({ ...q, [productId]: isNaN(n) || n < 0 ? 0 : n }));
  };

  const grandTotal = products.reduce((sum, p) => sum + (parseFloat(p.price) * (quantities[p.id] || 0)), 0);

  const handleSave = async () => {
    const items = products
      .filter(p => (quantities[p.id] || 0) > 0)
      .map(p => ({ product_id: p.id, quantity: quantities[p.id] }));

    if (items.length === 0) {
      toast.error('Εισάγετε ποσότητα για τουλάχιστον ένα προϊόν');
      return;
    }

    setSaving(true);
    try {
      await saveEntry(selectedFactory.id, { entry_date: date, notes, items });
      toast.success('Η καταχώρηση αποθηκεύτηκε!');
      loadEntry();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const categories = [...new Set(products.map(p => p.category || 'Άλλα'))];

  return (
    <>
      <div className="page-header">
        <h2>Καταχώρηση Ημέρας</h2>
        <p>Καταχωρήστε τις ποσότητες παραγωγής για την επιλεγμένη ημερομηνία</p>
      </div>
      <div className="page-body">
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <span className="card-title">Ημερομηνία & Σημειώσεις</span>
                {existingEntry && <span className="badge badge-green">✓ Υπάρχει καταχώρηση</span>}
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Ημερομηνία</label>
                  <input
                    type="date"
                    className="form-input"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Σημειώσεις (προαιρετικό)</label>
                  <textarea
                    className="form-textarea"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Π.χ. λειτούργησε μόνο πρωινή βάρδια..."
                    style={{ minHeight: 70 }}
                  />
                </div>
              </div>
            </div>

            <div className="total-bar">
              <span className="total-bar-label">Σύνολο Ημέρας</span>
              <span className="total-bar-amount">€{grandTotal.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><span className="loading-spinner" /> Αποθήκευση...</> : '💾 Αποθήκευση Καταχώρησης'}
            </button>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Προϊόντα</span>
              <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>{selectedFactory?.name}</span>
            </div>
            <div className="card-body" style={{ padding: '8px 20px' }}>
              {loading ? (
                <div className="page-loading"><span className="loading-spinner dark" /></div>
              ) : products.length === 0 ? (
                <div className="empty-state">
                  <div>📦</div>
                  <h3>Δεν υπάρχουν προϊόντα</h3>
                  <p>Προσθέστε προϊόντα από τη σελίδα Προϊόντα</p>
                </div>
              ) : (
                categories.map(cat => (
                  <div key={cat}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--gray-400)', fontWeight: 600, padding: '14px 0 6px', borderTop: cat !== categories[0] ? '1px solid var(--gray-100)' : 'none', marginTop: cat !== categories[0] ? 8 : 0 }}>
                      {cat}
                    </div>
                    {products.filter(p => (p.category || 'Άλλα') === cat).map(p => (
                      <div key={p.id} className="product-entry-row">
                        <div>
                          <div className="product-entry-name">{p.name}</div>
                          <div className="product-entry-price">€{parseFloat(p.price).toFixed(2)} / τεμ.</div>
                        </div>
                        <input
                          type="number"
                          className="qty-input"
                          min="0"
                          value={quantities[p.id] || ''}
                          placeholder="0"
                          onChange={e => handleQty(p.id, e.target.value)}
                        />
                        <div className="product-entry-total">
                          {quantities[p.id] > 0 ? `€${(parseFloat(p.price) * quantities[p.id]).toFixed(2)}` : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
